import { useId, type ReactNode } from 'react';
import { Button, StatusBadge, cn } from '@immo/ui-kit';
import { FLEET_HEALTH_WARN_PERCENT, type FleetHostHealth } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { useLocaleFormat } from '@/i18n/use-locale-format';
import { ServerIcon } from '@/components/layout/icons';
import { useCommandVerbCopy, useVerbLabel } from '../command-verbs';
import {
  clampPercent,
  formatFactor,
  formatGigabytes,
  isNearlyFull,
  LOAD_BAR_SCALE,
  limitsOf,
  loadOf,
  percentOf,
  pressureOf,
  temperatureOf,
} from './health-figures';
import { HealthBar } from './health-bar';

type Props = {
  readonly health: FleetHostHealth | null;
  readonly canCommand: boolean;
  /** A host command is in flight: the two buttons are replaced, not disabled, as the rest of the host verbs are. */
  readonly hostCommandPending: boolean;
  readonly onSystemPreview: () => void;
  readonly onSystemPrune: () => void;
};

/** The panel's health-state section: current figures in full, when they were measured, and the two disk commands. */
export function PanelHealth({ health, canCommand, hostCommandPending, onSystemPreview, onSystemPrune }: Props) {
  const t = useTranslations('pages.admin.fleet.health');
  const format = useLocaleFormat();
  const headingId = useId();
  const prune = useCommandVerbCopy('system-prune');
  const previewLabel = useVerbLabel('system-preview');
  const PruneIcon = prune.icon;
  const limits = limitsOf(health);

  const disk = percentOf(health?.disk ?? null);
  const memory = percentOf(health?.memory ?? null);
  const swap = percentOf(health?.swap ?? null);
  const pressure = pressureOf(health);
  const temperature = temperatureOf(health);
  const load = loadOf(health);

  const diskFull = disk.kind === 'value' && isNearlyFull(disk.percent);
  const memoryFull = memory.kind === 'value' && isNearlyFull(memory.percent);

  const usedOfTotal = (figure: typeof disk) =>
    figure.kind === 'value'
      ? t('used-of-total', {
          used: t('gigabytes', { value: formatGigabytes(figure.usedBytes) }),
          total: t('gigabytes', { value: formatGigabytes(figure.totalBytes) }),
          percent: figure.percent,
        })
      : null;

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-4">
      <h3 id={headingId} className="sr-only">
        {t('state-heading')}
      </h3>

      <FullRow
        label={t('disk')}
        badge={diskFull ? t('disk-full') : null}
        value={usedOfTotal(disk)}
        warning={diskFull}
        noData={t('no-data')}
        bar={
          <HealthBar
            fill={disk.kind === 'value' ? disk.percent : null}
            limit={FLEET_HEALTH_WARN_PERCENT}
            warning={diskFull}
            className="h-2"
          />
        }
      />
      <FullRow
        label={t('memory')}
        badge={memoryFull ? t('memory-full') : null}
        value={usedOfTotal(memory)}
        warning={memoryFull}
        noData={t('no-data')}
        bar={
          <HealthBar
            fill={memory.kind === 'value' ? memory.percent : null}
            limit={FLEET_HEALTH_WARN_PERCENT}
            warning={memoryFull}
            className="h-2"
          />
        }
      />
      <FullRow
        label={t('swap')}
        badge={null}
        value={usedOfTotal(swap)}
        warning={false}
        noData={t('no-data')}
        bar={<HealthBar fill={swap.kind === 'value' ? swap.percent : null} warning={false} className="h-2" />}
      />

      <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-3">
        <ThirdRow
          label={t('pressure')}
          value={pressure.kind === 'value' ? t('percent-value', { percent: pressure.percent }) : null}
          warning={pressure.kind === 'value' && pressure.overLimit}
          noData={t('no-data')}
          bar={
            <HealthBar
              fill={pressure.kind === 'value' ? clampPercent(pressure.percent) : null}
              limit={limits.memoryPressurePercent.throttleAt}
              resume={limits.memoryPressurePercent.resumeBelow}
              warning={pressure.kind === 'value' && pressure.overLimit}
              className="h-2"
            />
          }
        />
        <ThirdRow
          label={t('temperature')}
          value={temperature.kind === 'value' ? t('temperature-value', { degrees: temperature.degrees }) : null}
          warning={temperature.kind === 'value' && temperature.overLimit}
          noData={t('no-data')}
          bar={
            <HealthBar
              fill={temperature.kind === 'value' ? clampPercent(temperature.degrees) : null}
              limit={limits.temperatureC.throttleAt}
              resume={limits.temperatureC.resumeBelow}
              warning={temperature.kind === 'value' && temperature.overLimit}
              className="h-2"
            />
          }
        />
        <ThirdRow
          label={t('load')}
          value={load.kind === 'value' ? t('load-value', { factor: formatFactor(load.factor) }) : null}
          warning={load.kind === 'value' && load.overLimit}
          noData={t('no-data')}
          bar={
            <HealthBar
              fill={load.kind === 'value' ? clampPercent((load.factor / LOAD_BAR_SCALE) * 100) : null}
              limit={(limits.loadPerCore.throttleAt / LOAD_BAR_SCALE) * 100}
              resume={(limits.loadPerCore.resumeBelow / LOAD_BAR_SCALE) * 100}
              warning={load.kind === 'value' && load.overLimit}
              className="h-2"
            />
          }
        />
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        {health !== null && (
          <span className="text-sm text-text-tertiary">{t('measured', { time: format.ago(health.measuredAt) })}</span>
        )}
        {canCommand && !hostCommandPending && (
          <div className="flex flex-col gap-2 sm:ml-auto sm:flex-row">
            <Button type="button" size="sm" variant="secondary" className="w-full sm:w-auto" onClick={onSystemPreview}>
              <ServerIcon className="h-4 w-4" aria-hidden />
              {previewLabel}
            </Button>
            <Button
              type="button"
              size="sm"
              variant={diskFull ? 'danger' : 'secondary'}
              className="w-full sm:w-auto"
              onClick={onSystemPrune}
            >
              <PruneIcon className="h-4 w-4" aria-hidden />
              {prune.label}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}

function FullRow({
  label,
  badge,
  value,
  warning,
  noData,
  bar,
}: {
  readonly label: string;
  readonly badge: string | null;
  /** `null` = never reported. */
  readonly value: string | null;
  readonly warning: boolean;
  readonly noData: string;
  readonly bar: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <span className="flex items-center gap-2">
          <span className="text-base-sm font-medium text-text-primary">{label}</span>
          {badge !== null && <StatusBadge tone="warning" dot label={badge} />}
        </span>
        <Value value={value} warning={warning} noData={noData} />
      </div>
      {bar}
    </div>
  );
}

function ThirdRow({
  label,
  value,
  warning,
  noData,
  bar,
}: {
  readonly label: string;
  readonly value: string | null;
  readonly warning: boolean;
  readonly noData: string;
  readonly bar: ReactNode;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-base-sm font-medium text-text-primary">{label}</span>
        <Value value={value} warning={warning} noData={noData} />
      </div>
      {bar}
    </div>
  );
}

function Value({ value, warning, noData }: { value: string | null; warning: boolean; noData: string }) {
  if (value === null) return <span className="text-base-sm text-text-placeholder">{noData}</span>;

  return (
    <span
      className={cn(
        'whitespace-nowrap font-mono text-base-sm tabular-nums',
        warning ? 'font-medium text-warning-700' : 'text-text-primary',
      )}
    >
      {value}
    </span>
  );
}
